import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Lock, Eye, EyeOff, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const AUTH_KEY = 'scanit_admin_auth';
const AUTH_TIMESTAMP_KEY = 'scanit_admin_auth_ts';
const SESSION_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

const PasswordGate = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState(null);
  const [isLocked, setIsLocked] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      // Check if password is required
      const { data } = await axios.get(`${API}/admin/password-required`);
      
      if (!data.required) {
        // No password configured, allow access
        setIsAuthenticated(true);
        setIsLoading(false);
        return;
      }

      // Check if we have a stored session that hasn't expired
      const storedAuth = localStorage.getItem(AUTH_KEY);
      const storedTimestamp = localStorage.getItem(AUTH_TIMESTAMP_KEY);
      
      if (storedAuth && storedTimestamp) {
        const elapsed = Date.now() - parseInt(storedTimestamp, 10);
        
        // If session is still valid (within 24 hours), trust it without re-verifying
        // This prevents rate limiting issues on page refreshes
        if (elapsed < SESSION_DURATION) {
          setIsAuthenticated(true);
          setIsLoading(false);
          return;
        } else {
          // Session expired, clear it
          localStorage.removeItem(AUTH_KEY);
          localStorage.removeItem(AUTH_TIMESTAMP_KEY);
        }
      }
    } catch (error) {
      console.error('Auth check error:', error);
      // On network error, check if we have stored auth
      const storedAuth = localStorage.getItem(AUTH_KEY);
      if (storedAuth) {
        setIsAuthenticated(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password.trim()) {
      toast.error('Please enter a password');
      return;
    }

    setVerifying(true);
    setError(null);
    
    try {
      await axios.post(`${API}/admin/verify-password`, { password });
      
      // Store auth with timestamp
      localStorage.setItem(AUTH_KEY, password);
      localStorage.setItem(AUTH_TIMESTAMP_KEY, Date.now().toString());
      
      setIsAuthenticated(true);
      toast.success('Access granted');
    } catch (error) {
      console.error('Login error:', error.code, error.message);
      
      // Handle rate limiting / lockout
      if (error.response?.status === 429) {
        setIsLocked(true);
        setError('Too many failed attempts. Please wait 15 minutes before trying again.');
        toast.error('Account locked for 15 minutes', { duration: 5000 });
      } else if (error.response?.status === 401) {
        const detail = error.response?.data?.detail || 'Invalid password';
        setError(detail);
        toast.error(detail);
      } else if (error.code === 'ERR_NETWORK') {
        setError('Cannot connect to server. Check your internet connection.');
        toast.error('Connection failed. Check your internet.');
      } else if (error.code === 'ECONNABORTED') {
        setError('Request timed out. Server may be busy.');
        toast.error('Request timed out. Try again.');
      } else if (error.response?.status === 500) {
        setError('Server error. Please try again later.');
        toast.error('Server error. Try again later.');
      } else if (error.response?.status === 503) {
        setError('Service unavailable. Server is under maintenance.');
        toast.error('Service unavailable.');
      } else {
        setError(`Connection error: ${error.message || 'Unknown error'}`);
        toast.error('Connection error. Please try again.');
      }
      setPassword('');
    } finally {
      setVerifying(false);
    }
  };

  // Function to manually logout (can be called from admin dashboard)
  const logout = () => {
    localStorage.removeItem(AUTH_KEY);
    localStorage.removeItem(AUTH_TIMESTAMP_KEY);
    setIsAuthenticated(false);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (isAuthenticated) {
    return children;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6 pb-48 md:pb-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className="max-w-md w-full"
      >
        <div className="text-center mb-8">
          <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-6 ${isLocked ? 'bg-red-100' : 'bg-primary/10'}`}>
            {isLocked ? (
              <AlertCircle className="w-10 h-10 text-red-500" strokeWidth={1.5} />
            ) : (
              <Lock className="w-10 h-10 text-primary" strokeWidth={1.5} />
            )}
          </div>
          <h1 className="text-3xl font-bold font-heading tracking-tight mb-2">
            Admin Access
          </h1>
          <p className="text-muted-foreground">
            {isLocked ? 'Account temporarily locked' : 'Enter your password to continue'}
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter admin password"
              className="w-full border-2 border-border rounded-2xl bg-transparent px-4 py-4 pr-12 text-lg focus:outline-none focus:border-primary placeholder:text-muted-foreground/50 transition-colors"
              autoFocus
              disabled={isLocked}
              data-testid="admin-password-input"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            >
              {showPassword ? (
                <EyeOff className="w-5 h-5" strokeWidth={1.5} />
              ) : (
                <Eye className="w-5 h-5" strokeWidth={1.5} />
              )}
            </button>
          </div>

          <button
            type="submit"
            disabled={verifying || isLocked}
            className="w-full bg-primary text-primary-foreground rounded-full px-8 py-4 font-medium transition-all hover:scale-105 active:scale-95 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            data-testid="admin-login-button"
          >
            {verifying ? 'Verifying...' : isLocked ? 'Locked (wait 15 min)' : 'Access Admin'}
          </button>
        </form>

        {isLocked && (
          <p className="text-center text-sm text-muted-foreground mt-4">
            Try refreshing the page after 15 minutes, or contact support if the issue persists.
          </p>
        )}
      </motion.div>
    </div>
  );
};

export default PasswordGate;
